import type { ReactNode } from "react";
import { useDelayed } from "@/hooks/use-delayed";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarHeader, SidebarInset, SidebarMenu, SidebarMenuItem,
  SidebarMenuSkeleton, SidebarProvider,
} from "@/components/ui/sidebar";

/** Status region with a screen-reader label; renders nothing until `delay` ms have passed. */
export function Loading({ delay = 200, children }: { delay?: number; children: ReactNode }) {
  if (!useDelayed(true, delay)) return null;
  return (
    <div role="status" aria-live="polite">
      <span className="sr-only">Yükleniyor…</span>
      <div aria-hidden="true">{children}</div>
    </div>
  );
}

export function TableSkeleton({ rows = 5, cols = 4, delay }: { rows?: number; cols?: number; delay?: number }) {
  return (
    <Loading delay={delay}>
      <div className="space-y-2">
        {Array.from({ length: rows }, (_, r) => (
          <div key={r} className="flex gap-2">
            {Array.from({ length: cols }, (_, c) => <Skeleton key={c} className="h-6 flex-1" />)}
          </div>
        ))}
      </div>
    </Loading>
  );
}

export function CardSkeleton({ delay }: { delay?: number }) {
  return (
    <Loading delay={delay}>
      <div className="space-y-3 rounded-xl border bg-card p-4 shadow-xs">
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </Loading>
  );
}

/** StatCard anatomisini taklit eder: başlık çizgisi, ikon karesi, grafik karesi, büyük çizgi, alt çizgi (ui.md §12.6). */
export function StatCardSkeleton({ delay }: { delay?: number }) {
  return (
    <Loading delay={delay}>
      <div className="rounded-xl border bg-card shadow-xs">
        <div className="flex items-start justify-between px-4 pt-4"><Skeleton className="h-4 w-1/2" /><Skeleton className="size-9 rounded-lg" /></div>
        <div className="flex items-center gap-4 px-4 py-3"><Skeleton className="size-[72px]" /><Skeleton className="h-8 w-24" /></div>
        <div className="border-t px-4 py-3"><Skeleton className="h-4 w-1/3" /></div>
      </div>
    </Loading>
  );
}

export function PageSkeleton({ delay }: { delay?: number }) {
  return (
    <Loading delay={delay}>
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-1/4" />
        <div className="grid gap-4 sm:grid-cols-3">
          <Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" />
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    </Loading>
  );
}

/** Whole-app placeholder while the session loads or a redirect is pending. */
export function AppShellSkeleton({ delay }: { delay?: number }) {
  return (
    <SidebarProvider>
      <Sidebar>
        <SidebarHeader className="px-4 py-3 text-base font-semibold">YYS Tracker</SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarMenu>
              {Array.from({ length: 6 }, (_, i) => <SidebarMenuItem key={i}><SidebarMenuSkeleton /></SidebarMenuItem>)}
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>
      <SidebarInset><PageSkeleton delay={delay} /></SidebarInset>
    </SidebarProvider>
  );
}
